/**
 * The message above a form.
 *
 * `auth-login.html`'s own note is the spec: "one message above the form, never
 * at field level. It doesn't say which half was wrong, and it offers the reset
 * link inline instead of making the reader hunt for it."
 *
 * Read off the mockup's markup, not guessed: a bordered box at
 * `--mdx-radius-control`, `--mdx-space-12 --mdx-space-14` padding, a 22px round
 * mark, 15px text capped at `--mdx-measure-48`. The first build made it bare
 * coloured text and the screenshot comparison caught it.
 *
 * Two tones, and the difference is deliberate. `error` is `--mdx-error` on
 * WHITE — the token's own comment restricts red to "errors and destructive
 * actions only", and the mockup keeps its background white so red is an outline
 * rather than a wash. `notice` is `--mdx-review` on `--mdx-review-tint`, which
 * is what `auth-login.html` uses for the locked state: its note says "amber, not
 * red: nothing has broken and nothing is lost." A lockout is a wait, and
 * colouring it red would say otherwise.
 *
 * RULE-002 keeps red off the band ladder for the same reason; this is the same
 * discipline one layer down.
 *
 * Story 3.3, corrected against the reference in 3.4.
 */
import type { ReactNode } from 'react'

const TONE = {
  error: { edge: 'border-mdx-error', field: 'bg-mdx-white', mark: 'bg-mdx-error' },
  notice: { edge: 'border-mdx-review', field: 'bg-mdx-review-tint', mark: 'bg-mdx-review' },
} as const

export function Callout({
  tone = 'error',
  children,
}: {
  tone?: keyof typeof TONE
  children: ReactNode
}) {
  const { edge, field, mark } = TONE[tone]
  return (
    <div
      role="alert"
      className={`flex w-full items-start gap-mdx-12 rounded-mdx-control border px-mdx-14 py-mdx-12 text-left ${edge} ${field}`}
    >
      <span
        aria-hidden="true"
        className={`mt-px flex size-mdx-22 shrink-0 items-center justify-center rounded-mdx-pill text-mdx-14 font-mdx-bold text-mdx-white ${mark}`}
      >
        !
      </span>
      <span className="max-w-mdx-measure-48 text-mdx-15 text-mdx-ink">{children}</span>
    </div>
  )
}
